import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Navigate, useNavigate } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, Spinner } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api, enc } from "@/lib/api";
import { STAGE_CLS, label } from "@/lib/admin";
import { ReplyAlerts } from "@/components/outreach/ReplyAlerts";
import { cn } from "@/lib/utils";

const TABS = [
  ["/admin/cc", "Executive Overview"], ["/admin/vi", "Visitor Intelligence"], ["/admin/si", "Sourcing Intelligence"], ["/admin/rcc", "Revenue Command Center"], ["/admin/recruiters", "Recruiter Support"], ["/admin/cc/advisor", "AI Advisor"], ["/admin/cc/funnel", "Growth Funnel"], ["/admin/cc/profit", "Profitability"], ["/admin/cc/prospects", "Prospects CRM"], ["/admin/scans", "Scan Leads"], ["/admin/chat-leads", "Chat Leads"], ["/admin/cc/resume", "Resume Intelligence"],
  ["/admin/mk", "Email Marketing"], ["/admin/cc/revenue", "Revenue & Referrals"], ["/admin/cc/health", "Customer Health"],
  ["/admin/cc/ai", "AI Content Studio"], ["/admin/cc/email-verification", "Email Verification"], ["/admin/clients", "Client Admin"], ["/admin/access", "Team Access"], ["/admin/mailboxes", "Mailbox Sign-in"], ["/admin/sales-inbox", "Sales Inbox"], ["/admin", "Operations"],
];
const TAB_AREA = { "/admin/si": "si", "/admin/rcc": "rcc" };

export const StagePill = ({ stage, className }) => (
  <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", STAGE_CLS[stage] || "border-[#27272A] text-zinc-400", className)} data-testid="stage-pill">{label(stage)}</span>
);

const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const t = useRef();
  useEffect(() => {
    clearTimeout(t.current);
    if (q.trim().length < 2) { setRes(null); return; }
    t.current = setTimeout(() => {
      setBusy(true);
      api.get(`/admin/cc/search?q=${encodeURIComponent(q.trim())}`).then((r) => setRes(r.data?.items || [])).catch(() => setRes([])).finally(() => setBusy(false));
    }, 300);
  }, [q]);
  return (
    <div className="relative w-full max-w-sm" data-testid="cc-global-search">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or user ID" className="pl-9" data-testid="cc-search-input" />
      {busy && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-zinc-500" />}
      {res && (
        <div className="absolute z-40 mt-1 max-h-80 w-full overflow-auto rounded-xl border border-[#27272A] bg-[#121214] p-1 shadow-2xl" data-testid="cc-search-results">
          {res.length ? res.map((r) => (
            <button key={r.key} onClick={() => { setQ(""); nav(`/admin/cc/prospects/${enc(r.key)}`); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-[#18181B]" data-testid={`cc-search-result-${r.key}`}>
              <span className="min-w-0 flex-1"><span className="block truncate text-sm text-zinc-100">{r.name || r.email}</span><span className="block truncate text-xs text-zinc-500">{r.email}{r.tier ? ` · ${r.tier}` : ""}</span></span>
              {r.stage && <StagePill stage={r.stage} />}
            </button>
          )) : <p className="px-3 py-2 text-xs text-zinc-500">No matches.</p>}
        </div>
      )}
    </div>
  );
};

export const Pending = () => (
  <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="cc-backend-pending">
    Coming soon. This section fills in once the Command Center routes are live on your backend (they currently return "not found").
  </div>
);

export const Stat = ({ k, v, sub, tone, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-zinc-500">{k}</div>
    <div className={cn("mt-1 font-display text-2xl text-zinc-100", tone)}>{v}</div>
    {sub && <div className="mt-0.5 text-xs text-zinc-500">{sub}</div>}
  </Card>
);

export const Section = ({ title, children, right, className, testid }) => (
  <section className={cn("mb-8", className)} data-testid={testid}>
    <div className="mb-3 flex items-center justify-between gap-3"><h2 className="font-display text-lg text-zinc-100">{title}</h2>{right}</div>
    {children}
  </section>
);

export const Body = ({ q, children }) => (q.loading && !q.data ? <Spinner className="h-6 w-6 text-[#D4AF37]" /> : q.missing ? <Pending /> : q.error ? <p className="text-sm text-red-400" data-testid="cc-error">{q.error}</p> : children(q.data || {}));

export default function AdminShell({ title, subtitle, actions, children, area }) {
  const { isAdmin, canAdmin, client } = useAuth();
  if (!canAdmin(area)) return <Navigate to="/search" replace />;
  const tabs = isAdmin ? TABS : TABS.filter(([to]) => canAdmin(TAB_AREA[to]));
  return (
    <div data-testid="admin-shell">
      {!client && <><div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <Link to={isAdmin ? "/admin/cc" : tabs[0]?.[0] || "/search"} className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Admin Command Center</Link>
        {isAdmin && <GlobalSearch />}
      </div>
      <nav ref={(el) => el && requestAnimationFrame(() => el.querySelector('[aria-current="page"]')?.scrollIntoView({ inline: "center", block: "nearest" }))} className="-mx-1 mb-8 flex gap-1 overflow-x-auto pb-1" data-testid="cc-nav">
        {tabs.map(([to, l]) => (
          <NavLink key={to} to={to} end={!["/admin/cc/prospects", "/admin/rcc", "/admin/recruiters", "/admin/si"].includes(to)} data-testid={`cc-nav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            ref={(el) => el?.getAttribute("aria-current") === "page" && el.scrollIntoView({ block: "nearest", inline: "nearest" })}
            className={({ isActive }) => cn("shrink-0 rounded-full px-3.5 py-1.5 text-xs transition-colors", isActive ? "bg-[#D4AF37] font-semibold text-black" : "text-zinc-400 hover:bg-[#18181B] hover:text-zinc-100")}>{l}</NavLink>
        ))}
      </nav></>}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-3"><div className="min-w-0 flex-1"><h1 className="font-display text-3xl text-zinc-50 sm:text-4xl">{title}</h1>{subtitle && <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>}</div>
        {!client && <div className="sm:order-last"><ReplyAlerts /></div>}</div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}
